import log from '@uwm/logger';
import {useEffect} from 'react';
import {useSelector} from 'react-redux';

import useSsoLinkRefresh from '@/hooks/useSsoLinkRefresh';
import uwmMessageService from '@/services/uwm/uwmMessageService';
import {selectSsoLink} from '@/state/selectors/externalLinks';
import {getState, subscribe} from '@/state/store';
import type {ExternalLinkExtendedModel} from '@/types/aem';
import {formatByName} from '@/utils/stringUtils';

type Options = {
    externalLink?: ExternalLinkExtendedModel | null;
};

const DEFAULT_TIMEOUT = 10_000;

const blankLinkOpen = (url: string) => {
    if (!url) return;
    window.open(url, '_blank');
};

const getFormattedSsoTemplate = (externalLink?: ExternalLinkExtendedModel | null): string | null => {
    const {externalLinkCode, ssoTemplate, ssoUrl} = externalLink ?? {};
    const template = externalLinkCode || ssoTemplate || ssoUrl;

    if (!template) return null;
    return formatByName(template);
};

// Module-level batch buffer (debounced flush). Mirrors the legacy UWM Loyalty implementation.
type PendingEntry = {
    externalLink: ExternalLinkExtendedModel;
    formattedSsoTemplate: string | null;
};
let pendingExternalLinks: PendingEntry[] = [];
let flushTimerId: ReturnType<typeof setTimeout> | null = null;

const flushPending = () => {
    if (!pendingExternalLinks.length) return;
    uwmMessageService.sendGetExternalLinks(pendingExternalLinks.map((x) => ({...x})));
    pendingExternalLinks = [];
};

const scheduleFlush = () => {
    if (flushTimerId) clearTimeout(flushTimerId);
    flushTimerId = setTimeout(() => {
        flushTimerId = null;
        flushPending();
    }, 500);
};

const useSsoLinkFetch = ({
    externalLink,
    status,
    formattedSsoTemplate,
}: {
    externalLink?: ExternalLinkExtendedModel | null;
    status?: string;
    formattedSsoTemplate: string | null;
}) => {
    useEffect(() => {
        if (!externalLink) return;
        if (status) return;
        if (!formattedSsoTemplate) return;
        if (pendingExternalLinks.find((x) => x.formattedSsoTemplate === formattedSsoTemplate)) return;

        pendingExternalLinks.push({externalLink, formattedSsoTemplate});
        scheduleFlush();
    }, [formattedSsoTemplate, status]);
};

let timeoutId: ReturnType<typeof setTimeout> | null = null;
let unsubscribe: (() => void) | null = null;

const waitForExternalLink = (formattedSsoTemplate: string, timeout = DEFAULT_TIMEOUT): Promise<string> =>
    new Promise((resolve, reject) => {
        // Clear previous timeout and unsubscribe — only one wait in flight.
        if (timeoutId) clearTimeout(timeoutId);
        unsubscribe?.();

        const existing = selectSsoLink(formattedSsoTemplate)(getState());
        if (existing?.link) {
            resolve(existing.link);
            return;
        }

        timeoutId = setTimeout(() => {
            unsubscribe?.();
            reject(new Error('Timeout waiting for external link'));
        }, timeout);

        unsubscribe = subscribe(() => {
            const ssoLink = selectSsoLink(formattedSsoTemplate)(getState());
            if (ssoLink?.link) {
                if (timeoutId) clearTimeout(timeoutId);
                unsubscribe?.();
                resolve(ssoLink.link);
            }
        });
    });

/**
 * Returns a click handler that opens an external link. Mirrors the UWM Loyalty
 * `useExternalLinks` hook:
 *
 * 1. If a resolved SSO link exists in the store, open it directly.
 * 2. Otherwise, send `UWM_MODULE_GET_EXTERNAL_LINKS` to the parent app, wait
 *    for `UWM_INIT_EXTERNAL_LINKS` to populate the store, then open it.
 */
const useExternalLinks = ({externalLink}: Options): (() => void) | null => {
    const formattedSsoTemplate = getFormattedSsoTemplate(externalLink);
    const {link, status, expirationDate} = useSelector(selectSsoLink(formattedSsoTemplate));

    useSsoLinkFetch({externalLink, status, formattedSsoTemplate});
    useSsoLinkRefresh({expirationDate, formattedSsoTemplate});

    if (!externalLink || !formattedSsoTemplate) return null;

    if (link) {
        return () => blankLinkOpen(link);
    }

    return async () => {
        try {
            uwmMessageService.sendGetExternalLinks([{externalLink, formattedSsoTemplate}], true);

            const receivedLink = await waitForExternalLink(formattedSsoTemplate);

            if (receivedLink) {
                blankLinkOpen(receivedLink);
            }
        } catch (error) {
            // Match legacy behaviour: just log and let the user click again.
            log.info('Error redirecting to external link', error);
        }
    };
};

export default useExternalLinks;
